import styled from "@emotion/styled"
import { useEffect, useState } from "react"
import type { ReactNode } from "react"

import { MsEyebrow } from "../ms"
import { paper, fonts, shade } from "../theme"
import type { PlayerInfo } from "../protocol"
import { TvAvatar, TvTimer } from "./tvType"
import { isMirroredDisplay } from "../cast/castConfig"

/**
 * The TV canvas.
 *
 * Laid out at a fixed 1920×1080 and scaled to fit, rather than made responsive.
 * On a TV that's the better trade by some distance: the design is read from
 * three metres away, where what matters is that the proportions are exactly the
 * ones they were drawn at — and a living room contains every panel size and
 * aspect ratio there is. One transform handles all of them, and nothing in a
 * screen below has to think about it.
 *
 * `--tv-overscan` pulls the content in from the bezel. Plenty of TVs still crop
 * the outer few percent of the picture, and a leaderboard with its rank column
 * shaved off is a bug you can't fix from the sofa.
 */

const Host = styled.div`
    position: fixed;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${paper.inkDeep};
    overflow: clip;

    /* Vignette so the paper reads as a lit page rather than a bright rectangle
       floating in a dark room. */
    &::after {
        content: '';
        position: fixed;
        inset: 0;
        pointer-events: none;
        z-index: 3;
        background: radial-gradient(ellipse at center, transparent 30%, rgba(0,0,0,0.55) 100%);
    }
`

const Canvas = styled.div<{ mirrored?: boolean }>`
    position: relative;
    width: 1920px;
    height: 1080px;
    flex: none;
    transform-origin: center center;
    background: ${paper.paper};
    color: ${paper.ink};
    font-family: ${fonts.sans};
    overflow: clip;
    isolation: isolate;

    /*
     * The same grain the phone wears in index.css, scaled up: at 1920 the
     * phone's 240px tile reads as noise rather than paper.
     *
     * Dropped entirely when the board is being mirrored. Film grain is the
     * worst thing you can hand a video encoder — full-frame high-frequency
     * detail that never repeats, so it soaks up the bitrate that should be
     * describing the type, and the whole picture goes soft to pay for texture
     * nobody can see at streaming quality anyway. The gradients and vignette
     * stay: smooth ramps cost an encoder almost nothing.
     */
    &::before {
        display: ${p => p.mirrored ? "none" : "block"};
        content: '';
        position: absolute;
        inset: 0;
        pointer-events: none;
        z-index: 0;
        background-image:
            radial-gradient(circle at 18% 12%, ${shade(0.05)} 0, transparent 40%),
            radial-gradient(circle at 82% 78%, ${shade(0.06)} 0, transparent 45%),
            url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='320' height='320'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0.18  0 0 0 0 0.13  0 0 0 0 0.07  0 0 0 0.10 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>");
        background-size: auto, auto, 320px 320px;
        mix-blend-mode: multiply;
        opacity: 0.85;
    }

    &::after {
        content: '';
        position: absolute;
        inset: 0;
        pointer-events: none;
        z-index: 0;
        box-shadow: inset 0 0 160px ${shade(0.25)}, inset 0 0 320px ${shade(0.12)};
    }
`

/**
 * Status band, stage, roster strip.
 *
 * The band is a real row rather than something floating over the stage.
 * Absolute positioning is the obvious way to pin a corner and the wrong one —
 * it then sits on top of whatever the screen below puts in its top-right, which
 * for the vote tally and the standings leader is exactly where they want to be.
 */
const Frame = styled.div`
    position: relative;
    z-index: 2;
    display: grid;
    grid-template-rows: auto 1fr auto;
    height: 100%;
    padding: var(--tv-overscan, 0px);
`

/**
 * The stage is the TV's MsBody: one padded column that owns the screen between
 * the band and the strip. Same job, same name in spirit — it just never scrolls,
 * because nothing on a television should ask to be scrolled.
 */
export const TvStageArea = styled.div<{ center?: boolean }>`
    position: relative;
    display: flex;
    flex-direction: column;
    padding: 40px 80px 30px;
    min-height: 0;
    overflow: clip;
    ${p => p.center && `align-items: center; justify-content: center; text-align: center;`}
`

/* ── Status band: round · room · clock ──────────────────────────────────── */

const Band = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 40px;
    padding: 26px 80px 0;
    min-height: 76px;
`

const BandMeta = styled.div`
    display: flex;
    align-items: baseline;
    gap: 28px;
`

/**
 * Round and room set as one quiet line rather than three labelled blocks.
 *
 * The prototype gave each its own ROUND / ROOM / TIME eyebrow above a 56px
 * numeral, which is a lot of furniture to say "round 3, room ABCD" — and the
 * phone labels neither. `03 / 08` and a room code read as themselves.
 */
const BandFigure = styled.div<{ red?: boolean }>`
    font-family: ${fonts.serif};
    font-weight: 800;
    font-size: 30px;
    letter-spacing: -0.02em;
    font-variant-numeric: tabular-nums;
    color: ${p => p.red ? paper.red : paper.inkSoft};

    .of { color: ${paper.inkMute}; }
`

const pad2 = (n: number) => String(n).padStart(2, "0")

interface BandProps {
    round?: number | null
    totalRounds?: number | null
    code?: string | null
    timeoutTime?: number | null
    phaseStartTime?: number | null
}

export function TvBand({ round, totalRounds, code, timeoutTime, phaseStartTime }: BandProps) {
    return (
        <Band>
            <BandMeta>
                {round != null && round > 0 && (
                    <BandFigure>
                        {pad2(round)}
                        {totalRounds != null && <span className="of"> / {pad2(totalRounds)}</span>}
                    </BandFigure>
                )}
                {code && <BandFigure red>{code}</BandFigure>}
            </BandMeta>
            <TvTimer timeoutTime={timeoutTime} phaseStartTime={phaseStartTime} />
        </Band>
    )
}

/* ── Roster strip ───────────────────────────────────────────────────────── */

const Roster = styled.div`
    position: relative;
    background: ${paper.paperCool};
    padding: 18px 80px 22px;
    border-top: 1px solid ${shade(0.16)};

    /* Torn edge, the same gesture MsReceipt uses across the bottom of a phone
       card — flattened enough to read as a fold across a 1920px span. */
    &::before {
        content: '';
        position: absolute;
        left: 0;
        right: 0;
        top: -1px;
        height: 8px;
        background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='16' height='8'><path d='M0,0 L4,8 L8,0 L12,8 L16,0' fill='%23f4ecd8'/></svg>");
        background-size: 16px 8px;
        background-repeat: repeat-x;
        transform: scaleY(-1);
    }
`

const RosterRow = styled.div`
    display: flex;
    align-items: center;
    gap: 24px;
`

const Chips = styled.div`
    display: flex;
    gap: 12px;
    flex: 1;
    flex-wrap: wrap;
`

/**
 * A seat in the strip.
 *
 * Built on the phone's MsRow language rather than a bespoke pill: paper, a
 * hairline, the sticky-yellow `you` treatment reused for whoever holds the deck
 * — on a shared screen "you" has no meaning, but "the person everyone is
 * waiting on" does, and it's the same visual job.
 */
const Seat = styled.div<{ dealer?: boolean; away?: boolean }>`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 8px 18px 8px 8px;
    border-radius: 999px;
    background: ${p => p.dealer ? paper.ink : paper.paper};
    opacity: ${p => p.away ? 0.4 : 1};
    box-shadow: ${p => p.dealer
        ? `0 0 0 2px ${paper.ink}, 0 4px 12px -4px rgba(0,0,0,0.4)`
        : `0 0 0 1px ${shade(0.1)}, inset 0 1px 0 rgba(255,255,255,0.55), 0 4px 8px -4px ${shade(0.25)}`};
    transition: background 0.4s ease, box-shadow 0.4s ease, opacity 0.4s ease;
`

const SeatMeta = styled.div`
    display: flex;
    flex-direction: column;
    gap: 3px;
`

const SeatName = styled.div<{ onInk?: boolean }>`
    font-family: ${fonts.serif};
    font-weight: 700;
    font-size: 20px;
    line-height: 1;
    color: ${p => p.onInk ? paper.paper : paper.ink};
`

const SeatScore = styled.div<{ onInk?: boolean }>`
    font-family: ${fonts.sans};
    font-weight: 700;
    font-size: 13px;
    letter-spacing: 0.04em;
    font-variant-numeric: tabular-nums;
    color: ${p => p.onInk ? "rgba(244,236,216,0.6)" : paper.inkMute};

    .gain { color: ${paper.red}; margin-left: 5px; }
`

interface RosterProps {
    players: PlayerInfo[]
    /** Whoever the room is currently waiting on — picker, then presenter. */
    dealerId?: string | null
    scores?: Record<string, number>
    gains?: Record<string, number>
}

export function TvRoster({ players, dealerId, scores, gains }: RosterProps) {
    // Spectators aren't in the game; the strip is who's playing it.
    const seated = players.filter(p => p.role !== "spectator")

    return (
        <Roster>
            <RosterRow>
                <MsEyebrow tone="mute" style={{ fontSize: 14 }}>
                    Players · {seated.length}
                </MsEyebrow>
                <Chips>
                    {seated.map(p => {
                        const isDealer = !!dealerId && p.id === dealerId
                        const score = scores?.[p.id]
                        const gain = gains?.[p.id] ?? 0
                        return (
                            <Seat key={p.id} dealer={isDealer} away={p.isConnected === false}>
                                <TvAvatar size={38} you={isDealer}>{p.name.slice(0, 2)}</TvAvatar>
                                <SeatMeta>
                                    <SeatName onInk={isDealer}>{p.name}</SeatName>
                                    {score != null && (
                                        <SeatScore onInk={isDealer}>
                                            {score} PTS
                                            {gain > 0 && <span className="gain">+{gain}</span>}
                                        </SeatScore>
                                    )}
                                </SeatMeta>
                            </Seat>
                        )
                    })}
                </Chips>
            </RosterRow>
        </Roster>
    )
}

/* ── The stage itself ───────────────────────────────────────────────────── */

interface StageProps {
    children: ReactNode
    roster?: ReactNode
    band?: ReactNode
}

export function TvStage({ children, roster, band }: StageProps) {
    const scale = useFitScale()
    // Read once: a page doesn't change from mirrored to native mid-session.
    const [mirrored] = useState(isMirroredDisplay)

    return (
        <Host>
            <Canvas mirrored={mirrored} style={{ transform: `scale(${scale})` }}>
                <Frame>
                    {band ?? <div />}
                    <div style={{ position: "relative", minHeight: 0, display: "flex" }}>
                        {children}
                    </div>
                    {roster ?? <div />}
                </Frame>
            </Canvas>
        </Host>
    )
}

/** The largest scale at which the 1920×1080 canvas still fits the window. */
function useFitScale(): number {
    const [scale, setScale] = useState(() => fit())

    useEffect(() => {
        const onResize = () => setScale(fit())
        window.addEventListener("resize", onResize)
        // Orientation changes on a TV-shaped browser (and Cast's own resize on
        // first paint) don't always fire `resize` in time to be picked up.
        const id = setTimeout(onResize, 100)
        return () => {
            window.removeEventListener("resize", onResize)
            clearTimeout(id)
        }
    }, [])

    return scale
}

function fit(): number {
    if (typeof window === "undefined") return 1
    return Math.min(window.innerWidth / 1920, window.innerHeight / 1080)
}
